import { getIdToken } from './auth';

// In single-container mode (on-prem), VITE_BACKEND_URL is empty = same origin
// In development, defaults to localhost:8080
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? (import.meta.env.DEV ? 'http://localhost:8080' : '');

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Base API client that automatically includes auth token
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, headers = {}, ...fetchOptions } = options;

  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add auth token if available and not skipped
  if (!skipAuth) {
    try {
      const token = await getIdToken();
      if (token) {
        (requestHeaders as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get auth token:', error);
    }
  }

  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...fetchOptions,
    headers: requestHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle auth errors
    if (response.status === 401) {
      const error = new Error(errorData.error || 'Authentication required');
      (error as any).code = errorData.code || 'AUTH_ERROR';
      throw error;
    }

    if (response.status === 403) {
      const error = new Error(errorData.error || 'Access denied');
      (error as any).code = errorData.code || 'ACCESS_DENIED';
      throw error;
    }

    throw new Error(errorData.error || errorData.details || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * GET request
 */
export async function get<T = unknown>(endpoint: string, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
}

/**
 * POST request
 */
export async function post<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT request
 */
export async function put<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PATCH request
 */
export async function patch<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export async function del<T = unknown>(endpoint: string, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
}

/**
 * Upload FormData (multipart). Does NOT set Content-Type — browser handles boundary.
 */
export async function upload<T = unknown>(
  endpoint: string,
  formData: FormData,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, headers = {}, ...fetchOptions } = options;

  const requestHeaders: Record<string, string> = { ...(headers as Record<string, string>) };

  if (!skipAuth) {
    try {
      const token = await getIdToken();
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get auth token:', error);
    }
  }

  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...fetchOptions,
    method: 'POST',
    headers: requestHeaders,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 401) {
      const error = new Error(errorData.error || 'Authentication required');
      (error as any).code = errorData.code || 'AUTH_ERROR';
      throw error;
    }

    if (response.status === 403) {
      const error = new Error(errorData.error || 'Access denied');
      (error as any).code = errorData.code || 'ACCESS_DENIED';
      throw error;
    }

    throw new Error(errorData.error || errorData.details || `Request failed: ${response.status}`);
  }

  return response.json();
}

export default {
  get,
  post,
  put,
  patch,
  del,
  upload,
  request: apiRequest,
};
